/**
 * App-wide display timezone. Data, schedules, and logs are stored in Central Time (CST).
 * Users can choose a display timezone; default is America/Chicago.
 */

export const APP_DATA_TIMEZONE = "America/Chicago";

export const DEFAULT_TIMEZONE = APP_DATA_TIMEZONE;

export const TIMEZONE_STORAGE_KEY = "appDisplayTimezone";

/** Display timezone options shown in the navbar / settings. */
export const TIMEZONE_OPTIONS = [
  { value: "America/Chicago", label: "Central Time (CST/CDT)", abbr: "CT" },
  { value: "Europe/Berlin", label: "Central European (CET/CEST · GMT+1/+2)", abbr: "CET" },
  { value: "Europe/Istanbul", label: "Turkey / Moscow (TRT/MSK)", abbr: "TRT" },
  { value: "Africa/Nairobi", label: "East African Time (EAT)", abbr: "EAT" },
  { value: "America/New_York", label: "Eastern Time (EST/EDT)", abbr: "ET" },
  { value: "Asia/Manila", label: "Philippines Time (PHT)", abbr: "PHT" },
];

const isValidTimezone = (tz) => TIMEZONE_OPTIONS.some((o) => o.value === tz);

export const loadStoredTimezone = () => {
  try {
    const saved = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (saved && isValidTimezone(saved)) return saved;
  } catch {
    /* ignore */
  }
  return DEFAULT_TIMEZONE;
};

let displayTimezone = loadStoredTimezone();

export const getDisplayTimezone = () => displayTimezone;

export const setDisplayTimezone = (tz) => {
  if (!isValidTimezone(tz)) return;
  displayTimezone = tz;
  try {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, tz);
  } catch {
    /* ignore */
  }
};

export const getTimezoneLabel = (tz = displayTimezone) =>
  TIMEZONE_OPTIONS.find((o) => o.value === tz)?.label || tz;

export const getTimezoneAbbr = (tz = displayTimezone, date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value || getTimezoneOption(tz)?.abbr || tz;
  } catch {
    return getTimezoneOption(tz)?.abbr || tz;
  }
};

export const getTimezoneOption = (tz) => TIMEZONE_OPTIONS.find((o) => o.value === tz);

export const formatDisplayDate = (
  d,
  { month = "short", day = "numeric", year = "numeric", timeZone = displayTimezone } = {}
) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month, day, year, timeZone });
};

export const formatDisplayShortDate = (d, timeZone = displayTimezone) =>
  formatDisplayDate(d, { month: "short", day: "numeric", year: undefined, timeZone });

export const formatDisplayDateTime = (
  d,
  { timeZone = displayTimezone, dateStyle = "medium", timeStyle = "short" } = {}
) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", { dateStyle, timeStyle, timeZone });
};

export const formatDisplayWeekday = (d, timeZone = displayTimezone) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { weekday: "long", timeZone });
};

/** Weekday for a date-only field (no timezone shift). */
export const formatCalendarWeekday = (value, empty = "") => {
  const parsed = parseCalendarDate(value);
  if (!parsed) return empty;
  return parsed.toLocaleDateString("en-US", { weekday: "long" });
};

/** YYYY-MM-DD for `<input type="date">` from a date-only value. */
export const toCalendarDateInputValue = (value) => {
  const ymd = extractCalendarYmd(value);
  if (!ymd) return "";
  return `${ymd.y}-${String(ymd.m).padStart(2, "0")}-${String(ymd.d).padStart(2, "0")}`;
};

/** Today's calendar date as YYYY-MM-DD in the local timezone. */
export const todayCalendarInputValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

/** Compare two date-only values as YYYYMMDD integers (null-safe). */
export const calendarYmdKey = (value) => {
  const ymd = extractCalendarYmd(value);
  if (!ymd) return null;
  return ymd.y * 10000 + ymd.m * 100 + ymd.d;
};

/** Time-only display in the selected timezone (e.g. "3:45 PM"). */
export const formatDisplayTime = (
  d,
  { hour = "numeric", minute = "2-digit", timeZone = displayTimezone, ...rest } = {}
) => {
  if (!d) return "";
  return new Date(d).toLocaleTimeString("en-US", { hour, minute, timeZone, ...rest });
};

/**
 * Extract YYYY-MM-DD from a date-only value (string, Date, or ISO).
 * Uses the date portion as written — no timezone shift.
 */
export const extractCalendarYmd = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  }
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return null;

  // Stored date-only at UTC midnight or UTC noon → keep the UTC calendar day.
  if (
    (dt.getUTCHours() === 0 || dt.getUTCHours() === 12) &&
    dt.getUTCMinutes() === 0 &&
    dt.getUTCSeconds() === 0 &&
    dt.getUTCMilliseconds() === 0
  ) {
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
  }

  // Local calendar Date from parseCalendarDate / payroll math.
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
};

/**
 * Parse a calendar (date-only) value to a Date at local noon.
 * Avoids the UTC-midnight → previous-day bug for fields like payrollStartDate.
 */
export const parseCalendarDate = (value) => {
  const ymd = extractCalendarYmd(value);
  if (!ymd) return null;
  return new Date(ymd.y, ymd.m - 1, ymd.d, 12, 0, 0, 0);
};

/**
 * Format a date-only field without timezone conversion (calendar day as stored).
 */
export const formatCalendarDate = (
  value,
  { month = "short", day = "numeric", year = "numeric", empty = "—" } = {}
) => {
  const parsed = parseCalendarDate(value);
  if (!parsed) return empty;
  const opts = { month, day };
  if (year !== undefined) opts.year = year;
  return parsed.toLocaleDateString("en-US", opts);
};

/**
 * Generic display formatter in the selected timezone. Pass any Intl.DateTimeFormat
 * options (weekday/month/day/year/hour/minute/…); only the requested fields show.
 */
export const formatDisplay = (d, { locale = "en-US", timeZone = displayTimezone, empty = "—", ...opts } = {}) => {
  if (!d) return empty;
  return new Date(d).toLocaleString(locale, { timeZone, ...opts });
};

/** Format a calendar date for display (date-only fields). Uses display timezone. */
export const formatDisplayDateGB = (d, timeZone = displayTimezone) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone,
  });
};

/** Live clock parts in a timezone (for navbar). */
export const formatClockParts = (date = new Date(), timeZone = displayTimezone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  const hour = get("hour");
  const minute = get("minute");
  const second = get("second");
  const dayPeriod = get("dayPeriod");
  return {
    dateLabel: `${get("weekday")} ${get("month")} ${get("day")}`,
    timeLabel: `${hour}:${minute}:${second} ${dayPeriod}`.trim(),
    shortTimeLabel: `${hour}:${minute} ${dayPeriod}`.trim(),
    abbr: getTimezoneAbbr(timeZone, date),
  };
};

/**
 * Parse "9:00", "09:00", "9:00 AM", "13:30" into { hour (0-23), minute }.
 */
export const parseClockTimeString = (timeStr) => {
  if (!timeStr) return null;
  const raw = String(timeStr).trim();
  const m12 = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m12) {
    let hour = Number(m12[1]) % 12;
    if (/pm/i.test(m12[3])) hour += 12;
    return { hour, minute: Number(m12[2]) };
  }
  const m24 = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) return { hour: Number(m24[1]), minute: Number(m24[2]) };
  return null;
};

/**
 * Convert a wall-clock date+time in `timeZone` to a UTC Date.
 * Interview schedule times are stored as Central Time strings.
 */
export const zonedWallTimeToDate = (ymd, timeStr, timeZone = APP_DATA_TIMEZONE) => {
  const clock = parseClockTimeString(timeStr);
  if (!ymd || !clock) return null;
  const [y, mo, d] = String(ymd).split("-").map(Number);
  if (!y || !mo || !d) return null;

  const desiredAsUtc = Date.UTC(y, mo - 1, d, clock.hour, clock.minute, 0);

  const zoneWallAsUtc = (instant) => {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    const parts = Object.fromEntries(
      dtf
        .formatToParts(instant)
        .filter((p) => p.type !== "literal")
        .map((p) => [p.type, p.value])
    );
    return Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
  };

  // Find U where wall-clock in `timeZone` equals the desired local time.
  let utcMs = desiredAsUtc;
  for (let i = 0; i < 3; i++) {
    const offset = zoneWallAsUtc(new Date(utcMs)) - utcMs;
    utcMs = desiredAsUtc - offset;
  }
  return new Date(utcMs);
};

/** YYYY-MM-DD for a Date in a given IANA timezone. */
export const formatYmdInTimeZone = (date = new Date(), timeZone = APP_DATA_TIMEZONE) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

